/**
 * PaperRegionDetector v3 — brightness-blob + sector-extreme corners.
 *
 * Pure-JS image processing. No model dependency, no markers.
 *
 * Why hybrid (vs v1 brightness-only and v2 edge-only):
 *   v1 used a brightness percentile + flood fill, then picked the 4
 *     extreme diagonal points of the blob. Robust to text-heavy paper
 *     (the connected bright region captures the paper as a single blob
 *     with text as small interior holes), but diagonal extremes are
 *     fragile when a hand occludes part of a corner.
 *   v2 used Sobel + edge flood fill. Robust to lighting variation, but
 *     on a printed exam sheet the densely connected interior TEXT edges
 *     form the largest edge component — its bounding corners are the
 *     text region, not the paper outline. (2026-05-22 12:57 video.)
 *   v3 combines the strengths: brightness flood-fill to identify the
 *     paper region as a single blob (text is captured as part of the
 *     blob via 8-connectivity), then sector-extreme corner picking on
 *     that blob's boundary, which gives perspective-aware corners
 *     resilient to single-corner hand occlusion.
 *
 * Pipeline:
 *   1. Downscale ROI (lower 60% of frame) to ~256 px.
 *   2. Grayscale.
 *   3. Brightness threshold = max(70th percentile, floor 150) — handles
 *      both dim and bright scenes without picking up jacket shadows.
 *   4. Flood-fill largest bright connected component (paper blob).
 *   5. For every blob pixel adjacent to a non-blob pixel, mark as
 *      boundary. The boundary is the outer outline of the paper.
 *   6. Compute boundary centroid; in each of 4 quadrants relative to
 *      that centroid, pick the boundary pixel farthest from the
 *      centroid — these are the four corners.
 *   7. Sanity reject: must not touch ROI edges on ≥2 sides, must have
 *      reasonable aspect ratio.
 */

import type { Point2D } from './Homography';

export interface PaperRegionResult {
  /** Quadrilateral corners in normalized image coords, order TL/TR/BR/BL. */
  corners: [Point2D, Point2D, Point2D, Point2D];
  /** Heuristic 0..1 score: blob area / ROI area. */
  confidence: number;
}

/** Binary mask emitted by the most recent detector call for debug overlay. */
export interface PaperDebugSnapshot {
  /** ROI binary mask, length = sw * sh. 1 = paper candidate, 0 = not. */
  bin: Uint8Array;
  /** Subset of `bin` belonging to the selected blob, or null if none. */
  blob: Uint8Array | null;
  sw: number;
  sh: number;
  roiTopFrac: number;
  pass: number;
  params: PaperParams;
}

let lastDebugSnapshot: PaperDebugSnapshot | null = null;
export function getPaperDebugSnapshot(): PaperDebugSnapshot | null { return lastDebugSnapshot; }

interface PaperParams {
  satMax: number;
  valueMin: number;
  minPct: number;
}

const PARAM_PASSES: PaperParams[] = [
  // Pass 1 — strict, high specificity (paper-only)
  { satMax: 30, valueMin: 160, minPct: 0.005 },
  // Pass 2 — relaxed sat (warmer lighting environments)
  { satMax: 50, valueMin: 150, minPct: 0.005 },
  // Pass 3 — wider net for tough lighting / partially obscured paper
  { satMax: 80, valueMin: 140, minPct: 0.003 },
];

export function detectPaperRegion(
  video: HTMLVideoElement,
  offCanvas: HTMLCanvasElement,
): PaperRegionResult | null {
  // Multi-pass: try strict first, then progressively loosen.
  const perPass: Array<{ pass: number; params: PaperParams; result: PaperRegionResult | null; diag?: unknown }> = [];
  for (let i = 0; i < PARAM_PASSES.length; i++) {
    const params = PARAM_PASSES[i];
    const { result, diag } = detectOnceVerbose(video, offCanvas, params);
    perPass.push({ pass: i + 1, params, result, diag });
    if (result) {
      if (typeof window !== 'undefined') {
        (window as unknown as { __paperDetector?: unknown }).__paperDetector = {
          ok: true, pass: i + 1, params, conf: result.confidence, perPass,
        };
      }
      return result;
    }
  }
  if (typeof window !== 'undefined') {
    (window as unknown as { __paperDetector?: unknown }).__paperDetector = {
      ok: false, passes: PARAM_PASSES.length, perPass,
    };
  }
  return null;
}

function detectOnceVerbose(
  video: HTMLVideoElement,
  offCanvas: HTMLCanvasElement,
  params: PaperParams,
): { result: PaperRegionResult | null; diag: unknown } {
  // Wraps detectOnce + captures intermediate stats for diagnostic.
  const result = detectOnce(video, offCanvas, params);
  return { result, diag: null };
}

function detectOnce(
  video: HTMLVideoElement,
  offCanvas: HTMLCanvasElement,
  params: PaperParams,
): PaperRegionResult | null {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return null;

  // ROI: lower 75% of the frame (start 25% from top) so paper held at
  // upper chest is still captured. Walls extend above this line and get
  // rejected by the TOP-edge filter; the wider ROI doesn't hurt.
  const ROI_TOP_FRAC = 0.25;
  const roiYStart = Math.round(vh * ROI_TOP_FRAC);
  const roiH = vh - roiYStart;

  const TARGET_W = 256;
  const sw = TARGET_W;
  const sh = Math.max(64, Math.round((roiH / vw) * TARGET_W));
  if (offCanvas.width !== sw || offCanvas.height !== sh) {
    offCanvas.width = sw;
    offCanvas.height = sh;
  }
  const ctx = offCanvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(video, 0, roiYStart, vw, roiH, 0, 0, sw, sh);
  const img = ctx.getImageData(0, 0, sw, sh);

  // Saturation/brightness mask — paper is BRIGHT + LOW-SATURATION.
  const bin = new Uint8Array(sw * sh);
  for (let i = 0; i < bin.length; i++) {
    const j = i * 4;
    const r = img.data[j];
    const g = img.data[j + 1];
    const b = img.data[j + 2];
    const mx = Math.max(r, g, b);
    const mn = Math.min(r, g, b);
    if (mx >= params.valueMin && (mx - mn) <= params.satMax) bin[i] = 1;
  }

  // ── Flood-fill bright components; pick the paper blob.
  // Walls/ceiling lighting always extend to the TOP edge of the ROI (the
  // ROI starts ~40 % down the frame, at the wall behind the user). Real
  // paper held in front of the user never reaches that high. We reject
  // any blob touching the TOP edge — that's the wall. Bottom/left/right
  // edge touches are allowed because a held sheet can extend past the
  // frame on those sides without losing identity.
  // (2026-05-22 verification on user video: largest blob was the LEFT
  //  wall, second-largest the RIGHT wall, both touched TOP. The paper
  //  was a separate ~2 % blob touching only the BOTTOM edge.)
  const visited = new Uint8Array(sw * sh);
  let blob: number[] = [];
  const stack: number[] = [];
  const minPixels = Math.round(sw * sh * params.minPct);
  for (let y = 0; y < sh; y++) {
    for (let x = 0; x < sw; x++) {
      const idx = y * sw + x;
      if (bin[idx] !== 1 || visited[idx]) continue;
      const comp: number[] = [];
      let touchesTop = false;
      stack.length = 0;
      stack.push(idx);
      while (stack.length > 0) {
        const cur = stack.pop()!;
        if (visited[cur]) continue;
        visited[cur] = 1;
        comp.push(cur);
        const cx = cur % sw;
        const cy = (cur - cx) / sw;
        if (cy === 0) touchesTop = true;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const nx = cx + dx;
            const ny = cy + dy;
            if (nx < 0 || nx >= sw || ny < 0 || ny >= sh) continue;
            const nIdx = ny * sw + nx;
            if (bin[nIdx] === 1 && !visited[nIdx]) stack.push(nIdx);
          }
        }
      }
      if (touchesTop) continue;  // wall/ceiling, skip
      if (comp.length >= minPixels && comp.length > blob.length) {
        blob = comp;
      }
    }
  }

  if (blob.length < minPixels) return null;

  // ── Build a binary mask of the blob for fast boundary lookup ──
  const blobMask = new Uint8Array(sw * sh);
  for (const p of blob) blobMask[p] = 1;

  // ── Extract boundary pixels (4-connectivity) ──────────────
  const boundary: number[] = [];
  for (const p of blob) {
    const x = p % sw;
    const y = (p - x) / sw;
    const left   = x > 0      ? blobMask[p - 1]  : 0;
    const right  = x < sw - 1 ? blobMask[p + 1]  : 0;
    const up     = y > 0      ? blobMask[p - sw] : 0;
    const down   = y < sh - 1 ? blobMask[p + sw] : 0;
    if (!(left && right && up && down)) boundary.push(p);
  }
  if (boundary.length < 16) return null;

  // ── Diagonal-extreme corner extraction ────────────────────
  // Picks the 4 boundary points that maximize/minimize x+y and x-y.
  // ALWAYS produces 4 distinct corners for any non-degenerate blob,
  // unlike the sector method which fails when a paper held off-center
  // leaves one quadrant of the centroid empty (verified: 2026-05-22
  // 18:24 video, 4/5 frames had sector2=empty → algorithm returned null
  // despite a usable paper blob being present).
  //   TL = argmin(x + y)
  //   TR = argmax(x - y)
  //   BR = argmax(x + y)
  //   BL = argmin(x - y)
  let tlIdx = boundary[0], trIdx = boundary[0], brIdx = boundary[0], blIdx = boundary[0];
  let tlSum = Infinity, trDiff = -Infinity, brSum = -Infinity, blDiff = Infinity;
  for (const p of boundary) {
    const px = p % sw;
    const py = (p - px) / sw;
    const sum  = px + py;
    const diff = px - py;
    if (sum  < tlSum)  { tlSum  = sum;  tlIdx = p; }
    if (sum  > brSum)  { brSum  = sum;  brIdx = p; }
    if (diff > trDiff) { trDiff = diff; trIdx = p; }
    if (diff < blDiff) { blDiff = diff; blIdx = p; }
  }
  const sectorBest = [
    { idx: tlIdx }, { idx: trIdx }, { idx: brIdx }, { idx: blIdx },
  ];

  // Map indices to full-frame normalized coordinates.
  const yOffsetNorm = ROI_TOP_FRAC;
  const yScale = (1 - ROI_TOP_FRAC);
  const toPoint = (idx: number): Point2D => {
    const x = idx % sw;
    const y = (idx - x) / sw;
    return { x: x / sw, y: yOffsetNorm + (y / sh) * yScale };
  };
  const tl = toPoint(sectorBest[0].idx);
  const tr = toPoint(sectorBest[1].idx);
  const br = toPoint(sectorBest[2].idx);
  const bl = toPoint(sectorBest[3].idx);

  // Sanity: minimum width/height + reasonable aspect ratio. The earlier
  // edge-touch reject was too strict — paper held in hands often touches
  // left + bottom (corner extends past frame), which doesn't mean it isn't
  // paper. The blob's flood-fill TOP-edge skip already screens out walls.
  const widthN  = Math.hypot(tr.x - tl.x, tr.y - tl.y);
  const heightN = Math.hypot(bl.x - tl.x, bl.y - tl.y);
  if (widthN < 0.06 || heightN < 0.04) return null;
  const aspect = widthN / heightN;
  if (aspect < 0.2 || aspect > 6) return null;

  // Confidence = blob area / ROI area.
  const blobAreaFrac = blob.length / (sw * sh);
  const confidence = Math.max(0, Math.min(1, blobAreaFrac));

  return { corners: [tl, tr, br, bl], confidence };
}
